import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, Package, Calendar, Clock, CheckCircle, XCircle, 
  RefreshCw, MessageSquare, Send, User
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const RECLAMATION_TYPES = {
  colis_non_recu: { label: "Colis non reçu", color: "bg-red-100 text-red-700" },
  colis_endommage: { label: "Colis endommagé", color: "bg-orange-100 text-orange-700" },
  mauvaise_adresse: { label: "Mauvaise adresse", color: "bg-yellow-100 text-yellow-700" },
  sans_signature: { label: "Colis laissé sans signature", color: "bg-blue-100 text-blue-700" },
  retard: { label: "Retard de livraison", color: "bg-purple-100 text-purple-700" },
  autre: { label: "Autre", color: "bg-gray-100 text-gray-700" }
};

const STATUS_CONFIG = {
  non_traite: { label: "Non traité", color: "bg-red-100 text-red-700", icon: XCircle },
  en_cours: { label: "En cours", color: "bg-orange-100 text-orange-700", icon: Clock },
  traite_livreur: { label: "Traité par vous", color: "bg-blue-100 text-blue-700", icon: CheckCircle },
  traite: { label: "Clôturé", color: "bg-green-100 text-green-700", icon: CheckCircle }
};

const EmployeeReclamations = ({ user }) => {
  const [reclamations, setReclamations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchReclamations();
  }, []);

  const fetchReclamations = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/reclamations/my`, { withCredentials: true });
      setReclamations(res.data.reclamations || []);
    } catch (error) {
      console.error('Failed to fetch reclamations:', error);
      if (error.response?.status === 403) {
        setError("Cette fonctionnalité est réservée aux livreurs Colis Privé.");
      } else {
        setError("Erreur lors du chargement des réclamations.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetail = (rec) => {
    setSelectedReclamation(rec);
    setComment(rec.commentaire_livreur || '');
    setShowDetailModal(true);
  };

  const handleSubmitResponse = async (validate = false) => {
    if (!selectedReclamation) return;
    setSubmitting(true);
    try {
      await axios.put(
        `${API}/api/reclamations/${selectedReclamation.reclamation_id}/driver-response`,
        { commentaire_livreur: comment, validate },
        { withCredentials: true }
      );
      setShowDetailModal(false);
      fetchReclamations();
      alert(validate ? 'Réclamation marquée comme traitée !' : 'Commentaire enregistré');
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredReclamations = reclamations.filter(rec => {
    if (statusFilter === 'all') return true;
    return rec.status === statusFilter;
  });

  const stats = {
    total: reclamations.length,
    non_traite: reclamations.filter(r => r.status === 'non_traite').length,
    en_cours: reclamations.filter(r => r.status === 'en_cours').length,
    traite: reclamations.filter(r => r.status === 'traite' || r.status === 'traite_livreur').length,
    overdue: reclamations.filter(r => r.is_overdue).length
  };

  if (loading) {
    return (
      <Layout user={user} activeModule="b2b">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user} activeModule="b2b">
        <div className="flex flex-col items-center justify-center h-64 gap-4">
          <AlertTriangle className="w-16 h-16 text-orange-500" />
          <p className="text-lg text-slate-600">{error}</p>
          <Button onClick={fetchReclamations} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Réessayer
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} activeModule="b2b">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-7 h-7 text-orange-500" />
              Mes Réclamations
            </h1>
            <p className="text-slate-500 mt-1">Consultez et traitez vos réclamations clients</p>
          </div>
          <Button variant="outline" onClick={fetchReclamations}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-slate-50 to-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 font-medium">Total</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                </div>
                <Package className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600 font-medium">À traiter</p>
                  <p className="text-2xl font-bold text-red-900">{stats.non_traite}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-orange-600 font-medium">En cours</p>
                  <p className="text-2xl font-bold text-orange-900">{stats.en_cours}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Traités</p>
                  <p className="text-2xl font-bold text-green-900">{stats.traite}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className={`bg-gradient-to-br ${stats.overdue > 0 ? 'from-red-100 to-red-50 border-red-300' : 'from-yellow-50 to-white border-yellow-200'}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${stats.overdue > 0 ? 'text-red-700' : 'text-yellow-600'}`}>En retard</p>
                  <p className={`text-2xl font-bold ${stats.overdue > 0 ? 'text-red-900' : 'text-yellow-900'}`}>{stats.overdue}</p>
                </div>
                <AlertTriangle className={`w-8 h-8 ${stats.overdue > 0 ? 'text-red-600 animate-pulse' : 'text-yellow-500'}`} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="flex gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-lg bg-white text-sm"
          >
            <option value="all">Tous les statuts</option>
            <option value="non_traite">Non traité</option>
            <option value="en_cours">En cours</option>
            <option value="traite_livreur">Traité par moi</option>
            <option value="traite">Clôturé</option>
          </select>
        </div>

        {/* Reclamations List */}
        <div className="space-y-4">
          {filteredReclamations.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <p className="text-lg text-slate-600">
                  {statusFilter === 'all' 
                    ? 'Aucune réclamation pour le moment' 
                    : 'Aucune réclamation avec ce statut'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredReclamations.map((rec) => {
              const statusConfig = STATUS_CONFIG[rec.status] || STATUS_CONFIG.non_traite;
              const typeConfig = RECLAMATION_TYPES[rec.type_reclamation] || RECLAMATION_TYPES.autre;
              const StatusIcon = statusConfig.icon;

              return (
                <Card 
                  key={rec.reclamation_id} 
                  className={`cursor-pointer hover:shadow-md active:scale-[0.99] transition-all ${rec.is_overdue ? 'border-red-300 bg-red-50' : ''}`}
                  onClick={() => handleOpenDetail(rec)}
                  data-testid={`reclamation-card-${rec.reclamation_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <span className="font-mono font-bold text-base sm:text-lg">{rec.reference_colis}</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="w-3 h-3 inline mr-1" />
                            {statusConfig.label}
                          </span>
                          {rec.is_overdue && (
                            <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-200 text-red-800">
                              <AlertTriangle className="w-3 h-3 inline mr-1" />
                              +48h
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(rec.date_reclamation).toLocaleDateString('fr-FR')}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <User className="w-3.5 h-3.5" />
                            {rec.client_destinataire || 'Client non spécifié'}
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>
                            {typeConfig.label}
                          </span>
                        </div>
                        <p className="mt-2 text-sm text-slate-700 line-clamp-2">{rec.description}</p>
                      </div>
                      <Button 
                        variant="outline" 
                        onClick={(e) => { e.stopPropagation(); handleOpenDetail(rec); }}
                        className="min-h-[44px] w-full sm:w-auto shrink-0"
                        data-testid={`view-reclamation-${rec.reclamation_id}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Voir / Répondre
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg sm:max-w-lg w-[calc(100%-16px)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg">Détail de la réclamation</DialogTitle>
          </DialogHeader>
          {selectedReclamation && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-slate-500">Référence colis</label>
                  <p className="font-mono font-bold">{selectedReclamation.reference_colis}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Date</label>
                  <p>{new Date(selectedReclamation.date_reclamation).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-slate-500">Client / Destinataire</label>
                <p>{selectedReclamation.client_destinataire || 'Non spécifié'}</p>
              </div>

              {/* Adresse du client */}
              <div className="bg-slate-50 p-3 rounded-lg" data-testid="reclamation-client-address">
                <label className="text-xs font-medium text-slate-500 block mb-1">Adresse du client</label>
                <p className="text-sm text-slate-700">
                  {selectedReclamation.client_adresse || <span className="italic text-slate-400">Non renseignée</span>}
                </p>
                <p className="text-sm text-slate-700">
                  {selectedReclamation.client_code_postal || ''} {selectedReclamation.client_ville || ''}
                  {!selectedReclamation.client_code_postal && !selectedReclamation.client_ville && (
                    <span className="italic text-slate-400">Code postal / Ville non renseignés</span>
                  )}
                </p>
              </div>

              {/* PDA utilisé */}
              <div data-testid="reclamation-pda-numero">
                <label className="text-xs font-medium text-slate-500">PDA utilisé</label>
                <p className={`font-mono font-medium ${selectedReclamation.pda_numero ? 'text-slate-800' : 'italic text-slate-400'}`}>
                  {selectedReclamation.pda_numero || 'Non renseigné'}
                </p>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-500">Type de réclamation</label>
                <p className={`inline-block px-2 py-1 rounded-full text-sm ${RECLAMATION_TYPES[selectedReclamation.type_reclamation]?.color || ''}`}>
                  {RECLAMATION_TYPES[selectedReclamation.type_reclamation]?.label || selectedReclamation.type_reclamation}
                </p>
              </div>
              
              <div>
                <label className="text-xs font-medium text-slate-500">Description</label>
                <p className="text-slate-700 bg-slate-50 p-3 rounded-lg">{selectedReclamation.description}</p>
              </div>
              
              {selectedReclamation.commentaire_interne && (
                <div>
                  <label className="text-xs font-medium text-slate-500">Note de l'administration</label>
                  <p className="text-slate-700 bg-blue-50 p-3 rounded-lg">{selectedReclamation.commentaire_interne}</p>
                </div>
              )}
              
              <div>
                <label className="text-xs font-medium text-slate-500 mb-2 block">Votre commentaire</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ajoutez votre explication ou commentaire..."
                  className="w-full px-3 py-2 border rounded-lg resize-none"
                  rows={3}
                  disabled={selectedReclamation.status === 'traite'}
                />
              </div>
            </div>
          )}
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowDetailModal(false)} className="min-h-[44px] w-full sm:w-auto order-last sm:order-first">
              Fermer
            </Button>
            {selectedReclamation && selectedReclamation.status !== 'traite' && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => handleSubmitResponse(false)}
                  disabled={submitting || !comment}
                  className="min-h-[44px] w-full sm:w-auto"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enregistrer commentaire
                </Button>
                <Button 
                  onClick={() => handleSubmitResponse(true)}
                  disabled={submitting}
                  className="bg-green-600 hover:bg-green-700 min-h-[48px] w-full sm:w-auto text-base font-semibold"
                  data-testid="validate-reclamation-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {submitting ? 'En cours...' : 'Valider comme traité'}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EmployeeReclamations;
